import React from 'react';
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Box,
  Chip,
  Avatar,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import {
  Security,
  Description,
  PrivacyTip,
  Cookie,
  Gavel,
  Policy,
} from '@mui/icons-material';

const PoliciesSection: React.FC = () => {
  const policies = [
    {
      title: 'Privacy Policy',
      description: 'How we collect, use, and protect your personal information with industry-leading security standards.',
      link: '/privacy',
      icon: <PrivacyTip />,
      color: '#667eea',
      gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    },
    {
      title: 'Terms of Service',
      description: 'The terms and conditions governing your use of MetroRahi and our comprehensive service agreement.',
      link: '/terms',
      icon: <Gavel />,
      color: '#4CAF50',
      gradient: 'linear-gradient(135deg, #4CAF50 0%, #45a049 100%)',
    },
    {
      title: 'Data Protection',
      description: 'Our commitment to protecting your data and privacy with enterprise-grade encryption and security.',
      link: '/data-protection',
      icon: <Security />,
      color: '#FF9800',
      gradient: 'linear-gradient(135deg, #FF9800 0%, #F57C00 100%)',
    },
    {
      title: 'Cookie Policy',
      description: 'How we use cookies to improve your experience and provide personalized metro navigation services.',
      link: '/cookies',
      icon: <Cookie />,
      color: '#E91E63',
      gradient: 'linear-gradient(135deg, #E91E63 0%, #C2185B 100%)',
    },
  ];

  return (
    <Box sx={{ py: 12, bgcolor: '#1a1a2e' }}>
      <Container maxWidth="lg">
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <Chip
            label="Legal"
            sx={{
              bgcolor: 'rgba(102, 126, 234, 0.2)',
              color: '#667eea',
              fontWeight: 600,
              mb: 2,
              border: '1px solid rgba(102, 126, 234, 0.3)',
            }}
          />
          <Typography
            variant="h2"
            component="h2"
            sx={{
              fontWeight: 800,
              mb: 3,
              fontSize: { xs: '2.5rem', md: '3.5rem' },
              color: 'white',
            }}
          >
            Legal & Policies
          </Typography>
          <Typography
            variant="h6"
            sx={{
              color: 'rgba(255,255,255,0.8)',
              fontWeight: 400,
              maxWidth: 600,
              mx: 'auto',
              lineHeight: 1.6,
            }}
          >
            We are committed to transparency and protecting your rights. Review our comprehensive legal framework.
          </Typography>
        </Box>

        {/* Policies Grid */}
        <Grid container spacing={4}>
          {policies.map((policy, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'all 0.3s ease',
                  borderRadius: 4,
                  border: '1px solid rgba(255,255,255,0.1)',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
                  bgcolor: '#0f0f23',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 12px 40px rgba(0,0,0,0.5)',
                    '& .policy-icon': {
                      transform: 'scale(1.1)',
                    },
                  },
                  overflow: 'visible',
                }}
              >
                <CardContent sx={{ flexGrow: 1, p: 4, textAlign: 'center' }}>
                  {/* Icon */}
                  <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                    <Avatar
                      className="policy-icon"
                      sx={{
                        width: 70,
                        height: 70,
                        background: policy.gradient,
                        transition: 'transform 0.3s ease',
                        boxShadow: `0 8px 25px ${policy.color}40`,
                      }}
                    >
                      {policy.icon}
                    </Avatar>
                  </Box>

                  {/* Title */}
                  <Typography
                    variant="h5"
                    component="h3"
                    sx={{
                      mb: 2,
                      fontWeight: 700,
                      color: 'white',
                    }}
                  >
                    {policy.title}
                  </Typography>

                  {/* Description */}
                  <Typography
                    variant="body1"
                    sx={{
                      color: 'rgba(255,255,255,0.8)',
                      lineHeight: 1.6,
                      mb: 3,
                      fontSize: '0.95rem',
                    }}
                  >
                    {policy.description}
                  </Typography>
                </CardContent>

                <CardActions sx={{ p: 4, pt: 0 }}>
                  <Button
                    size="large"
                    component={RouterLink}
                    to={policy.link}
                    sx={{
                      bgcolor: policy.color,
                      color: 'white',
                      borderRadius: 3,
                      px: 3,
                      py: 1.5,
                      fontWeight: 600,
                      '&:hover': {
                        bgcolor: policy.color,
                        transform: 'translateY(-2px)',
                        boxShadow: `0 8px 25px ${policy.color}40`,
                      },
                      transition: 'all 0.3s ease',
                    }}
                  >
                    Learn More
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Bottom CTA */}
        <Box sx={{ textAlign: 'center', mt: 8 }}>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 600,
              mb: 3,
              color: 'white',
            }}
          >
            Have questions about our policies?
          </Typography>
          <Button
            variant="outlined"
            size="large"
            startIcon={<Policy />}
            sx={{
              borderColor: '#667eea',
              color: '#667eea',
              borderRadius: 3,
              px: 4,
              py: 1.5,
              fontWeight: 600,
              '&:hover': {
                borderColor: '#5a6fd8',
                bgcolor: '#667eea',
                color: 'white',
                transform: 'translateY(-2px)',
              },
              transition: 'all 0.3s ease',
            }}
            component="a"
            href="mailto:legal@metrorahi.com"
          >
            Contact Legal Team
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default PoliciesSection; 